/**
 * UnitCard — Phase 8f-bcs (2026-05-07)
 * ------------------------------------
 * v2 schema migration:
 *   • dropped: view, baths, balconies, expectedRent
 *   • added:   status badge (available / reserved / sold)
 *   • yield chip now reads unit.rentScenarios.mid
 */
import { BedDouble, Maximize, Check } from 'lucide-react';
import FloorPlanImage from '@components/FloorPlanImage';
import { useT, type StringKey } from '@lib/i18n';
import { formatOMR, getMidScenario } from '@lib/pricing';
import type { Unit, UnitStatus } from '@lib/types';

interface UnitCardProps {
  unit: Unit;
  selected?: boolean;
  variant?: 'compact' | 'expanded';
  onSelect: (u: Unit) => void;
}

export default function UnitCard({
  unit,
  selected = false,
  variant = 'compact',
  onSelect,
}: UnitCardProps) {
  const t = useT();
  const expanded = variant === 'expanded';
  const isAvailable = unit.status === 'available';
  const mid = getMidScenario(unit);

  // Visual treatment for non-available units — opacity + cursor.
  const statusOverlayCls =
    unit.status === 'sold'
      ? 'opacity-40 cursor-not-allowed pointer-events-none'
      : unit.status === 'reserved'
        ? 'opacity-60 cursor-not-allowed pointer-events-none'
        : '';

  const handleSelect = () => {
    if (!isAvailable) return;
    onSelect(unit);
  };

  return (
    <article
      className={[
        'card overflow-hidden flex flex-col transition-all duration-200',
        isAvailable ? 'cursor-pointer hover:shadow-md' : '',
        expanded ? 'md:col-span-2 lg:col-span-3' : '',
        selected
          ? 'ring-2 ring-gold border-transparent shadow-md'
          : isAvailable ? 'hover:border-navy-300' : '',
        statusOverlayCls,
      ].join(' ')}
      onClick={handleSelect}
      role="button"
      tabIndex={isAvailable ? 0 : -1}
      onKeyDown={(e) => {
        if (!isAvailable) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleSelect();
        }
      }}
      aria-pressed={selected}
      aria-disabled={!isAvailable}
    >
      <div className="relative">
        <FloorPlanImage unit={unit} size={expanded ? 'large' : 'thumb'} />
        <div className="absolute top-2 start-2 flex items-center gap-1.5">
          <Badge tone="gold" label={unit.unitCode} mono />
          <Badge tone="navy" label={unit.type} mono />
        </div>
        {!isAvailable && (
          <StatusBadge status={unit.status} t={t} />
        )}
        {isAvailable && selected && (
          <div className="absolute top-2 end-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gold text-navy text-xs font-bold">
            <Check className="w-3 h-3" strokeWidth={3} />
            {t('match.selectedBadge')}
          </div>
        )}
      </div>

      <div className="p-4 flex-1 flex flex-col gap-3">
        <SpecRow unit={unit} t={t} />

        <div className="flex items-end justify-between gap-3">
          <div>
            <div className="text-2xl md:text-3xl font-bold text-navy">
              <span className="num-en">{formatOMR(unit.basePrice, { withSymbol: false })}</span>
              <span className="text-sm font-normal text-navy-500 ms-1">{t('common.omr')}</span>
            </div>
            <div className="text-xs text-navy-500 mt-0.5">
              {t('match.floorLabel')}: <span className="text-navy-700">{unit.floor}</span>
            </div>
          </div>

          <div className="text-end">
            <YieldChip
              monthlyRent={mid.monthlyRent}
              paybackYears={mid.paybackYears}
              t={t}
            />
          </div>
        </div>

        {expanded && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleSelect();
            }}
            className={`btn-primary mt-2 ${(selected || !isAvailable) ? 'opacity-60 cursor-default' : ''}`}
            disabled={selected || !isAvailable}
          >
            {selected ? t('match.selectedBadge') : t('match.chooseThis')}
          </button>
        )}
      </div>
    </article>
  );
}

function StatusBadge({
  status,
  t,
}: {
  status: UnitStatus;
  t: (k: StringKey) => string;
}) {
  if (status === 'reserved') {
    return (
      <div className="absolute top-2 end-2 px-2 py-1 rounded-md bg-red-600 text-white text-xs font-bold shadow">
        {t('match.statusReserved')}
      </div>
    );
  }
  if (status === 'sold') {
    return (
      <div className="absolute top-2 end-2 px-2 py-1 rounded-md bg-navy-600 text-white text-xs font-bold shadow">
        {t('match.statusSold')}
      </div>
    );
  }
  return null;
}

function SpecRow({
  unit,
  t,
}: {
  unit: Unit;
  t: (k: StringKey) => string;
}) {
  return (
    <div className="grid grid-cols-2 gap-2 text-xs text-navy-700">
      <Spec
        icon={<Maximize className="w-3.5 h-3.5" />}
        value={
          <>
            <span className="num-en">{unit.area}</span> {t('match.areaUnit')}
          </>
        }
      />
      <Spec
        icon={<BedDouble className="w-3.5 h-3.5" />}
        value={
          <>
            <span className="num-en">{unit.beds}</span>{' '}
            <span className="text-navy-500">{t('match.bedsLabel')}</span>
          </>
        }
      />
    </div>
  );
}

function Spec({ icon, value }: { icon: React.ReactNode; value: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1 text-navy-600">
      <span className="text-navy-400">{icon}</span>
      <span className="font-medium text-navy-800">{value}</span>
    </div>
  );
}

function YieldChip({
  monthlyRent,
  paybackYears,
  t,
}: {
  monthlyRent: number;
  paybackYears: number;
  t: (k: StringKey) => string;
}) {
  return (
    <div className="text-xs text-navy-600">
      <div>
        <span className="num-en">{formatOMR(monthlyRent, { withSymbol: false })}</span>{' '}
        <span className="text-navy-400">{t('common.omr')}</span>
        <span className="text-navy-400">/{t('common.month')}</span>
      </div>
      <div className="text-navy-500 mt-0.5">
        {t('match.paybackLabel')}{' '}
        <span className="num-en font-semibold text-navy-700">{paybackYears.toFixed(1)}</span>{' '}
        {t('match.paybackYears')}
      </div>
    </div>
  );
}

function Badge({
  tone,
  label,
  mono = false,
}: {
  tone: 'gold' | 'navy';
  label: string;
  mono?: boolean;
}) {
  const toneCls =
    tone === 'gold' ? 'bg-gold text-navy' : 'bg-navy/90 text-gold';
  return (
    <span
      className={`${toneCls} ${mono ? 'num-en' : ''} text-xs font-bold px-2 py-0.5 rounded-md backdrop-blur-sm`}
    >
      {label}
    </span>
  );
}
